import { FC, ReactElement, ReactNode, useCallback, useState } from 'react';
import { CustomButton } from '@medtronic/pecc-react-component-library-js';
import { Box, FormControlLabel, Modal, Stack, Switch, Typography } from '@mui/material';
import {
  removeButtonStyles,
  removeUserModalContainerStyles,
} from '~/pages/DatasetDetailsPage/components/DatasetModal/styles';
import { useAppDispatch } from '~/redux';
import { closeModalById } from '~/redux/slices/modalSlice';
import { ModalHeader } from '..';
import { modalActionBlockStyles, modalContainerStyle, modalSmallWidthStyles } from '../styles';
import { switchBlockStyles } from './styles';

export enum DeleteTypeEnum {
  FILES = 'files',
  FOLDER = 'folder',
  ETL_JOB = 'etl-job',
  DATASET_ACCESS = 'dataset-access',
  NOTICE_BANNER = 'notice-banner',
}

export interface DeleteConfirmationModalProps {
  payload: {
    id: string;
    onDelete: () => void;
    onClose?: () => void;
    type: DeleteTypeEnum;
    itemName?: string;
    content?: ReactElement;
    itemsCount?: number;
    deleteButtonLabel?: string;
    cancelButtonLabel?: string;
    isDangerStyle: boolean;
  };
}

const DeleteConfirmationModal: FC<DeleteConfirmationModalProps> = ({
  payload: {
    onDelete,
    onClose,
    type,
    itemName,
    content,
    itemsCount = 1,
    deleteButtonLabel = 'Delete',
    cancelButtonLabel = 'Keep',
    isDangerStyle = true,
    id,
  },
}) => {
  const dispatch = useAppDispatch();
  const [checked, setChecked] = useState<boolean>(false);
  const isPlural = itemsCount > 1;

  const textConfig: Record<DeleteTypeEnum, { title: string; message: ReactNode | string }> = {
    [DeleteTypeEnum.FILES]: {
      title: `Delete file${isPlural ? 's' : ''}`,
      message: `Are you sure you want to delete the selected file${isPlural ? 's' : ''}? This action will delete all the files permanently.`,
    },
    [DeleteTypeEnum.FOLDER]: {
      title: 'Delete folder',
      message: `Are you sure you want to delete the ${itemName ? `"${itemName}" folder` : 'folder'}? This action will delete all the sub folder and files within it.`,
    },
    [DeleteTypeEnum.ETL_JOB]: {
      title: 'Delete ETL Job',
      message: (
        <Typography>
          Are you sure you want to delete the <strong>"{itemName}"</strong> job?
        </Typography>
      ),
    },
    [DeleteTypeEnum.DATASET_ACCESS]: {
      title: 'Revoke Dataset Access',
      message: `Would you like to revoke access to the dataset?${!itemsCount ? '' : ` If so, the user will lose the access and won’t be able to access the dataset in the below project${isPlural ? 's' : ''}?`} `,
    },
    [DeleteTypeEnum.NOTICE_BANNER]: {
      title: 'Delete Banner',
      message: 'Are you sure you wanted to delete the banner?',
    },
  };

  const handleClose = useCallback(() => {
    setChecked(false);
    onClose?.();
    dispatch(closeModalById(id));
  }, [dispatch, id]);

  return (
    <Modal open onClose={handleClose}>
      <Box
        sx={{
          ...(isDangerStyle ? removeUserModalContainerStyles : modalContainerStyle),
          ...modalSmallWidthStyles,
        }}
      >
        <ModalHeader onClose={handleClose} title={[textConfig[type].title]} style={{ borderRadius: 0 }} />
        <Stack py={2} px={3} spacing={2}>
          <Typography sx={{ wordBreak: 'break-word' }}>{textConfig[type].message}</Typography>
          {content}
          <FormControlLabel
            labelPlacement="end"
            label="I understand and wish to proceed with this action"
            sx={switchBlockStyles}
            control={
              <Switch onClick={(e) => e.stopPropagation()} onChange={() => setChecked(!checked)} sx={{ mx: '1rem' }} />
            }
          />
        </Stack>
        <Box sx={modalActionBlockStyles}>
          <CustomButton buttonType="borderless" label={cancelButtonLabel} onClick={handleClose} />
          <CustomButton
            sx={isDangerStyle ? removeButtonStyles : undefined}
            label={deleteButtonLabel}
            disabled={!checked}
            onClick={() => {
              onDelete();
              handleClose();
            }}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default DeleteConfirmationModal;
